import api from './api';

// Data source schema for external data widgets
export interface DataSourceColumnSchema {
  name: string;
  dtype: string;  // 'string', 'number', 'integer', 'date', 'boolean'
  nullable: boolean;
}

export interface DataSourceSchemaSnapshot {
  sourceId: string;
  sourceName: string;
  columns: DataSourceColumnSchema[];
  rowCount?: number;
  capturedAt: string;
}

// Export types
export interface WidgetTemplateExport {
  schemaType: 'orchestrate/widget-template/v1';
  originalId: string;
  name: string;
  description?: string;
  type: string;
  defaultConfig: Record<string, unknown>;
  // For external data widgets, includes schema of the data source
  dataSourceSchema?: DataSourceSchemaSnapshot;
}

/** Widget instance in a dashboard with embedded template */
export interface WidgetInstanceExport {
  id: string;
  title: string;
  templateId: string;
  configOverrides: Record<string, unknown>;
  /** Full widget template configuration embedded */
  template?: WidgetTemplateExport;
}

export interface DashboardExport {
  schemaType: 'orchestrate/dashboard/v1';
  originalId: string;
  name: string;
  description?: string;
  layoutConfig: Record<string, unknown>;
  /** Widget instances with embedded template details */
  widgets: WidgetInstanceExport[];
  /** Dashboard data is always empty in exports - proprietary data is NOT exported */
  data: Record<string, unknown>;
}

export interface WorkflowExport {
  schemaType: 'orchestrate/workflow/v1';
  originalId: string;
  name: string;
  description?: string;
  /** Flow data with widgetConfigs stripped of proprietary content (only structure preserved) */
  flowData: Record<string, unknown>;
  /** Embedded dashboards with their widget templates */
  dashboards: DashboardExport[];
}

export interface BundleExport {
  schemaType: 'orchestrate/bundle/v1';
  exportedAt: string;
  version: string;
  workflow?: WorkflowExport;
  dashboards: DashboardExport[];
  widgetTemplates: WidgetTemplateExport[];
}

// Import types
export type ConflictResolution = 'rename' | 'skip' | 'overwrite';

export interface ImportOptions {
  conflictResolution: ConflictResolution;
  importAsDraft: boolean;
}

export interface WidgetTemplateImport {
  name: string;
  description?: string;
  type: string;
  defaultConfig: Record<string, unknown>;
  originalId?: string;
}

/** Widget instance with optional embedded template */
export interface WidgetInstanceImport {
  id: string;
  title: string;
  templateId: string;
  configOverrides: Record<string, unknown>;
  /** If template is provided, it will be imported/matched */
  template?: WidgetTemplateImport;
}

export interface DashboardImport {
  name: string;
  description?: string;
  layoutConfig: Record<string, unknown>;
  /** Widget instances with optional embedded templates */
  widgets: WidgetInstanceImport[];
  /** Dashboard data - typically empty from exports (proprietary data not included) */
  data: Record<string, unknown>;
  originalId?: string;
}

export interface WorkflowImport {
  name: string;
  description?: string;
  flowData: Record<string, unknown>;
  originalId?: string;
  /** Embedded dashboards with their widget templates */
  dashboards?: DashboardImport[];
}

export interface BundleImport {
  workflow?: WorkflowImport;
  dashboards: DashboardImport[];
  widgetTemplates: WidgetTemplateImport[];
  options: ImportOptions;
}

// Result types
export interface ImportedItemResult {
  originalId?: string;
  newId: string;
  name: string;
  status: 'created' | 'skipped' | 'overwritten';
  message?: string;
}

export interface ImportResult {
  success: boolean;
  widgetsImported: ImportedItemResult[];
  dashboardsImported: ImportedItemResult[];
  workflowsImported: ImportedItemResult[];
  errors: string[];
  idMapping: Record<string, string>;
}

// Preview types
export interface ImportPreviewItem {
  name: string;
  type: 'widget' | 'dashboard' | 'workflow';
  originalId?: string;
  hasConflict: boolean;
  existingId?: string;
  existingName?: string;
}

export interface ImportPreview {
  widgets: ImportPreviewItem[];
  dashboards: ImportPreviewItem[];
  workflows: ImportPreviewItem[];
  totalItems: number;
  conflictsCount: number;
  validationErrors: string[];
}

// Helper to download JSON as file
const downloadJson = (data: unknown, filename: string) => {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// Helper to parse imported JSON file
export const parseImportFile = async (file: File): Promise<unknown> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        const data = JSON.parse(content);
        resolve(data);
      } catch (err) {
        reject(new Error('Invalid JSON file'));
      }
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });
};

// Detect what type of export file we have
export const detectExportType = (data: unknown): 'widget' | 'dashboard' | 'workflow' | 'bundle' | 'unknown' => {
  if (!data || typeof data !== 'object') return 'unknown';

  const schemaType = (data as Record<string, unknown>).schemaType;

  if (schemaType === 'orchestrate/widget-template/v1') return 'widget';
  if (schemaType === 'orchestrate/dashboard/v1') return 'dashboard';
  if (schemaType === 'orchestrate/workflow/v1') return 'workflow';
  if (schemaType === 'orchestrate/bundle/v1') return 'bundle';

  return 'unknown';
};

// Helper to extract widget templates from embedded dashboard widgets
const extractWidgetTemplates = (widgets: WidgetInstanceExport[]): WidgetTemplateImport[] => {
  const templates: WidgetTemplateImport[] = [];
  const seen = new Set<string>();

  for (const widget of widgets) {
    if (widget.template && !seen.has(widget.templateId)) {
      templates.push({
        name: widget.template.name,
        description: widget.template.description,
        type: widget.template.type,
        defaultConfig: widget.template.defaultConfig,
        originalId: widget.template.originalId,
      });
      seen.add(widget.templateId);
    }
  }

  return templates;
};

// Helper to convert widget instances to import format
const convertWidgetInstances = (widgets: WidgetInstanceExport[]): WidgetInstanceImport[] => {
  return widgets.map(w => ({
    id: w.id,
    title: w.title,
    templateId: w.templateId,
    configOverrides: w.configOverrides || {},
    template: w.template ? {
      name: w.template.name,
      description: w.template.description,
      type: w.template.type,
      defaultConfig: w.template.defaultConfig,
      originalId: w.template.originalId,
    } : undefined,
  }));
};

// Convert export format to import format
export const convertToImportFormat = (data: unknown, type: string): BundleImport => {
  const defaultOptions: ImportOptions = {
    conflictResolution: 'rename',
    importAsDraft: true,
  };

  switch (type) {
    case 'widget': {
      const widget = data as WidgetTemplateExport;
      return {
        widgetTemplates: [{
          name: widget.name,
          description: widget.description,
          type: widget.type,
          defaultConfig: widget.defaultConfig,
          originalId: widget.originalId,
        }],
        dashboards: [],
        options: defaultOptions,
      };
    }
    case 'dashboard': {
      const dashboard = data as DashboardExport;
      // Extract embedded widget templates
      const widgetTemplates = extractWidgetTemplates(dashboard.widgets);

      return {
        dashboards: [{
          name: dashboard.name,
          description: dashboard.description,
          layoutConfig: dashboard.layoutConfig,
          widgets: convertWidgetInstances(dashboard.widgets),
          data: dashboard.data || {},
          originalId: dashboard.originalId,
        }],
        widgetTemplates,
        options: defaultOptions,
      };
    }
    case 'workflow': {
      const workflow = data as WorkflowExport;
      // Extract dashboards and their widget templates from embedded data
      const allTemplates: WidgetTemplateImport[] = [];
      const dashboardImports: DashboardImport[] = [];

      for (const d of workflow.dashboards || []) {
        const templates = extractWidgetTemplates(d.widgets);
        allTemplates.push(...templates);

        dashboardImports.push({
          name: d.name,
          description: d.description,
          layoutConfig: d.layoutConfig,
          widgets: convertWidgetInstances(d.widgets),
          data: d.data || {},
          originalId: d.originalId,
        });
      }

      return {
        workflow: {
          name: workflow.name,
          description: workflow.description,
          flowData: workflow.flowData,
          originalId: workflow.originalId,
          dashboards: dashboardImports,
        },
        dashboards: dashboardImports,
        widgetTemplates: allTemplates,
        options: defaultOptions,
      };
    }
    case 'bundle': {
      const bundle = data as BundleExport;
      // Extract templates from dashboards if not provided at top level
      const allTemplates: WidgetTemplateImport[] = bundle.widgetTemplates?.map(w => ({
        name: w.name,
        description: w.description,
        type: w.type,
        defaultConfig: w.defaultConfig,
        originalId: w.originalId,
      })) || [];

      // Also extract from embedded dashboard widgets
      for (const d of bundle.dashboards || []) {
        const embedded = extractWidgetTemplates(d.widgets);
        for (const t of embedded) {
          if (!allTemplates.find(existing => existing.originalId === t.originalId)) {
            allTemplates.push(t);
          }
        }
      }

      const dashboardImports = (bundle.dashboards || []).map(d => ({
        name: d.name,
        description: d.description,
        layoutConfig: d.layoutConfig,
        widgets: convertWidgetInstances(d.widgets),
        data: d.data || {},
        originalId: d.originalId,
      }));

      return {
        workflow: bundle.workflow ? {
          name: bundle.workflow.name,
          description: bundle.workflow.description,
          flowData: bundle.workflow.flowData,
          originalId: bundle.workflow.originalId,
          dashboards: dashboardImports,
        } : undefined,
        dashboards: dashboardImports,
        widgetTemplates: allTemplates,
        options: defaultOptions,
      };
    }
    default:
      throw new Error('Unknown export format');
  }
};

export const exportImportService = {
  // ============== EXPORT ==============

  exportWidget: async (widgetId: string): Promise<void> => {
    const response = await api.get<WidgetTemplateExport>(`/export-import/widgets/${widgetId}/export`);
    const filename = `${response.data.name.replace(/\s+/g, '_')}_widget.json`;
    downloadJson(response.data, filename);
  },

  exportDashboard: async (dashboardId: string): Promise<void> => {
    const response = await api.get<DashboardExport>(`/export-import/dashboards/${dashboardId}/export`);
    const filename = `${response.data.name.replace(/\s+/g, '_')}_dashboard.json`;
    downloadJson(response.data, filename);
  },

  exportDashboardBundle: async (dashboardId: string): Promise<void> => {
    const response = await api.get<BundleExport>(`/export-import/dashboards/${dashboardId}/export/bundle`);
    const dashboardName = response.data.dashboards[0]?.name || 'dashboard';
    const filename = `${dashboardName.replace(/\s+/g, '_')}_bundle.json`;
    downloadJson(response.data, filename);
  },

  exportWorkflow: async (workflowId: string): Promise<void> => {
    const response = await api.get<WorkflowExport>(`/export-import/workflows/${workflowId}/export`);
    const filename = `${response.data.name.replace(/\s+/g, '_')}_workflow.json`;
    downloadJson(response.data, filename);
  },

  exportWorkflowBundle: async (workflowId: string): Promise<void> => {
    const response = await api.get<BundleExport>(`/export-import/workflows/${workflowId}/export/bundle`);
    const workflowName = response.data.workflow?.name || 'workflow';
    const filename = `${workflowName.replace(/\s+/g, '_')}_bundle.json`;
    downloadJson(response.data, filename);
  },

  // ============== IMPORT ==============

  previewImport: async (bundle: BundleImport): Promise<ImportPreview> => {
    const response = await api.post<ImportPreview>('/export-import/preview', bundle);
    return response.data;
  },

  importWidget: async (widget: WidgetTemplateImport, options?: ImportOptions): Promise<ImportResult> => {
    const response = await api.post<ImportResult>('/export-import/widgets', widget, {
      params: options,
    });
    return response.data;
  },

  importDashboard: async (dashboard: DashboardImport, options?: ImportOptions): Promise<ImportResult> => {
    const response = await api.post<ImportResult>('/export-import/dashboards', dashboard, {
      params: options,
    });
    return response.data;
  },

  importWorkflow: async (workflow: WorkflowImport, options?: ImportOptions): Promise<ImportResult> => {
    const response = await api.post<ImportResult>('/export-import/workflows', workflow, {
      params: options,
    });
    return response.data;
  },

  importBundle: async (bundle: BundleImport): Promise<ImportResult> => {
    const response = await api.post<ImportResult>('/export-import/bundle', bundle);
    return response.data;
  },
};

export default exportImportService;
